import Box from '@mui/material/Box';
import { Fragment, useMemo } from 'react';

import {
  PageImageLightboxProvider,
  usePageImageLightbox,
} from 'src/common/components/PageImageLightboxContext';
import { usePublicPageEdit } from 'src/common/components/PublicPageEditContext';
import { renderPageBlock } from 'src/common/components/page-blocks/renderPageBlock';
import { wrapPageBlockWithSpacing } from 'src/common/components/PageBlockSpacingWrapper';
import { LanguageKeys } from 'src/common/constants/languages';
import { evaluateVisibilityFilter, VisibilityContext } from 'src/helpers/visibilityFilter';
import { PlaceholderRenderContext } from 'src/helpers/placeholderRegistry';
import { collectImagesFromPageBlocks } from 'src/helpers/pageImageUtils';
import { shouldApplyBlockVisibilityRules } from 'src/helpers/pageContentUtils';
import { shouldRenderTextBlockInline } from 'src/helpers/signupPageLayoutUtils';
import { IEdition } from 'src/interfaces/Edition';
import { IPageBlock, ITextBlock } from 'src/interfaces/PageBlock';
import { PublicPageShell } from 'src/interfaces/PublicPage';
import { useAuth } from 'src/auth/useAuth';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  blocks: IPageBlock[];
  lang: LanguageKeys;
  contentKey: string;
  layoutShell?: PublicPageShell | null;
  inHero?: boolean;
}

function renderBlock(
  block: IPageBlock,
  lang: LanguageKeys,
  placeholderContext: PlaceholderRenderContext,
  currentEdition: IEdition | undefined,
  visibilityContext: VisibilityContext,
  inHero: boolean | undefined,
  applyVisibilityRules: boolean,
  contentKey: string,
) {
  const node = wrapPageBlockWithSpacing(
    block,
    renderPageBlock(block, {
      lang,
      placeholderContext,
      currentEdition,
      visibilityContext,
      inHero,
      applyVisibilityRules,
      contentKey,
    }),
  );
  if (node == null) {
    return null;
  }
  return <Fragment key={block.id}>{node}</Fragment>;
}

export default function PublicPageBlocksView({
  blocks,
  lang,
  contentKey,
  layoutShell,
  inHero,
}: Props) {
  const { user } = useAuth();
  const edit = usePublicPageEdit();
  const isEditMode = edit?.isEditMode ?? false;
  const { activeEditionId, dateFormat, editionsById } = useSystemOptionsStore(s => s);

  const currentEdition =
    editionsById && activeEditionId ? editionsById[activeEditionId] : undefined;

  const visibilityContext: VisibilityContext = {
    currentEdition,
    activeEditionId,
    userLoggedIn: Boolean(user),
  };

  const placeholderContext: PlaceholderRenderContext = {
    currentEdition,
    dateFormat: dateFormat[lang],
  };

  const applyVisibilityRules = shouldApplyBlockVisibilityRules(contentKey, layoutShell);
  const parentLightbox = usePageImageLightbox();
  const pageImages = useMemo(
    () => collectImagesFromPageBlocks(blocks, lang),
    [blocks, lang],
  );

  const content = (
    <Box>
      {blocks.map(block => {
        if (
          block.type === 'text' &&
          !shouldRenderTextBlockInline(block as ITextBlock, isEditMode)
        ) {
          return null;
        }
        if (block.type === 'results') {
          return renderBlock(
            block,
            lang,
            placeholderContext,
            currentEdition,
            visibilityContext,
            inHero,
            applyVisibilityRules,
            contentKey,
          );
        }
        if (
          applyVisibilityRules &&
          !isEditMode &&
          !evaluateVisibilityFilter(block.visibility, visibilityContext)
        ) {
          return null;
        }
        return renderBlock(
          block,
          lang,
          placeholderContext,
          currentEdition,
          visibilityContext,
          inHero,
          applyVisibilityRules,
          contentKey,
        );
      })}
    </Box>
  );

  if (isEditMode || parentLightbox) {
    return content;
  }

  return (
    <PageImageLightboxProvider images={pageImages}>
      {content}
    </PageImageLightboxProvider>
  );
}
